/*
 * ====  A POINT ABOUT THE IMPORTS  ===========================================
 *
 *  This class is used, amongst other places, on the IdentifierPublicPage[1]
 *  where the user may not be authenticated. As such, this module, and any
 *  module that is imported, MUST NOT import anything from the global Inventory
 *  stores (i.e. from ../stores/*). If it does, then the page will be rendered
 *  as a blank screen and there will be an unhelpful error message on the
 *  browser's console saying that a module export could not be initialised. To
 *  avoid this, dependency injection is used to pass references that have been
 *  created in code that can depend on the global stores.
 *
 *  [1]: ../../components/PublicPage/IdentifierPublicPage.js
 *
 * ============================================================================
 */
import { format, isValid } from "date-fns";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import type React from "react";
import i18n from "@/modules/common/i18n";
import TransRichText from "@/modules/common/i18n/TransRichText";
import { getErrorMessage } from "@/util/error";
import type InvApiService from "../../common/InvApiService";
import type { RadioOption } from "../../components/Inputs/RadioField";
import type { _LINK, URL } from "../../util/types";
import { type Alert, mkAlert } from "../contexts/Alert";
import type { GlobalId, Id } from "../definitions/BaseRecord";
import type { GeoLocation, GeoLocationPolygon } from "../definitions/GeoLocation";
import type {
  AlternateIdentifier,
  CreatorType,
  DropdownOption,
  ExternalMetadataUpdate,
  Identifier,
  IdentifierAttrs,
  IdentifierDate,
  IdentifierDescription,
  IdentifierField,
  IdentifierSubject,
  PublishingState,
} from "../definitions/Identifier";
import { identifierStateLabelKey } from "../definitions/Identifier";
import GeoLocationModel from "./GeoLocationModel";

/*
 * The alert reports the state in the same words the identifiers table shows, so the two cannot
 * disagree. The model has no component `t`, so it resolves the key on the shared i18n instance.
 * An unrecognised provider status has no key and degrades to the raw value.
 */
const stateLabelOf = (state: PublishingState): string => {
  const key = identifierStateLabelKey(state);
  return key === null ? String(state) : i18n.t(key);
};

type GeoLocationBox = {
  eastBoundLongitude: string;
  northBoundLatitude: string;
  southBoundLatitude: string;
  westBoundLongitude: string;
};
type PolygonPoint = {
  pointLatitude: string;
  pointLongitude: string;
};

export type IdentifierGeoLocation = {
  geoLocationBox: GeoLocationBox;
  geoLocationPlace: string;
  geoLocationPoint: PolygonPoint;
  geoLocationPolygon: GeoLocationPolygon;
};

/*
 * These are functions rather than module constants so the labels resolve when
 * the fields are rendered (after i18n has initialised and the inventory
 * namespace has loaded, and re-resolving on a language change via the consuming
 * component's `useTranslation`) instead of being frozen to whatever was
 * available at import time.
 */
const getCreatorTypeOptions = (): Array<RadioOption<string>> => [
  {
    value: "Personal",
    label: i18n.t("inventory:identifierModel.creatorTypes.personal"),
  },
  {
    value: "Organizational",
    label: i18n.t("inventory:identifierModel.creatorTypes.organizational"),
  },
];

const getIdentifierDescriptionOptions = (): Array<DropdownOption> => [
  { value: "ABSTRACT", label: i18n.t("inventory:identifierModel.descriptionTypes.abstract") },
  { value: "METHODS", label: i18n.t("inventory:identifierModel.descriptionTypes.methods") },
];
const getIdentifierDateOptions = (): Array<DropdownOption> => [
  { value: "ACCEPTED", label: i18n.t("inventory:identifierModel.dateTypes.accepted") },
  { value: "AVAILABLE", label: i18n.t("inventory:identifierModel.dateTypes.available") },
  { value: "COPYRIGHTED", label: i18n.t("inventory:identifierModel.dateTypes.copyrighted") },
  { value: "COLLECTED", label: i18n.t("inventory:identifierModel.dateTypes.collected") },
  { value: "CREATED", label: i18n.t("inventory:identifierModel.dateTypes.created") },
  { value: "ISSUED", label: i18n.t("inventory:identifierModel.dateTypes.issued") },
  { value: "SUBMITTED", label: i18n.t("inventory:identifierModel.dateTypes.submitted") },
  { value: "UPDATED", label: i18n.t("inventory:identifierModel.dateTypes.updated") },
  { value: "VALID", label: i18n.t("inventory:identifierModel.dateTypes.valid") },
  { value: "WITHDRAWN", label: i18n.t("inventory:identifierModel.dateTypes.withdrawn") },
  { value: "OTHER", label: i18n.t("inventory:identifierModel.dateTypes.other") },
];

/**
 * subFields is a mapping function,
 * useful to help rendering in the MultipleInputHandler component.
 * more fields could be added in the future,
 * and their schema is defined by datacite, not RS.
 * this function lets us extract all properties different from "value" and "type".
 */
export const subFields = <Key extends string, Value, Field extends Record<Key, Value>>(
  field: Field,
): Array<{ key: Key; value: Value }> =>
  (Object.entries(field) as Array<[Key, Value]>)
    .filter((item) => item[0] !== "value" && item[0] !== "type")
    .map((item) => {
      return { key: item[0], value: item[1] };
    });

export const subFieldsForNew: object = {
  Subjects: {
    subjectScheme: "",
    schemeURI: "",
    valueURI: "",
    classificationCode: "",
  },
  "Alternate Identifiers": {
    freeType: "",
  },
};

/*
 * DataCite identifier-metadata field labels. These are intentionally NOT
 * translated and must stay in English: `subjectScheme`, `schemeURI`, `valueURI`
 * and `classificationCode` are verbatim DataCite Metadata Schema attribute names
 * (Subject property), and "Type" corresponds to DataCite's alternateIdentifierType.
 * Keeping them in English preserves consistency with the DataCite schema and the
 * export/deposit metadata that references these terms.
 * See https://datacite-metadata-schema.readthedocs.io/ (6. Subject).
 */
export const RECOMMENDED_FIELDS_LABELS = {
  type: "Type",
  freeType: "Type",
  subjectScheme: "Subject Scheme",
  schemeURI: "Scheme URI",
  valueURI: "Value URI",
  classificationCode: "Classification Code",
};

export default class IdentifierModel implements Identifier {
  parentGlobalId: GlobalId;
  id: Id;
  rsPublicId: string | null;
  doi: string;
  doiType: string;
  creatorName: string;
  creatorType: CreatorType;
  creatorAffiliation: string | null;
  creatorAffiliationIdentifier: string | null;
  title: string; // item.name
  publicUrl: URL | null;
  providerUrl: URL | null;
  publisher: string;
  publicationYear: string;
  resourceType: string;
  resourceTypeGeneral: string;
  url: URL | null;
  state: PublishingState = "draft";
  subjects: Array<IdentifierSubject> | null = [];
  descriptions: Array<IdentifierDescription> | null = [];
  alternateIdentifiers: Array<AlternateIdentifier> | null = [];
  dates: Array<IdentifierDate> | null = [];
  geoLocations: ReadonlyArray<GeoLocation> | null = [];
  _links: Array<_LINK> = [];
  editing: boolean = false;
  customFieldsOnPublicPage: boolean;
  linked: boolean;
  /*
   * Deliberately absent from the makeObservable map below, unlike every sibling field: it is read
   * once, imperatively, right after a save (see InventoryBaseRecord.update) and never rendered, so
   * observing it would buy nothing and would make plain assignment to it an out-of-action write.
   */
  externalMetadataUpdate: ExternalMetadataUpdate | null = null;

  ApiServiceBase: typeof InvApiService | null = null;

  constructor(attrs: IdentifierAttrs, parentGlobalId: GlobalId, ApiServiceBase?: typeof InvApiService) {
    makeObservable(this, {
      parentGlobalId: observable,
      id: observable,
      rsPublicId: observable,
      doi: observable,
      doiType: observable,
      creatorName: observable,
      creatorType: observable,
      creatorAffiliation: observable,
      creatorAffiliationIdentifier: observable,
      title: observable,
      publicUrl: observable,
      providerUrl: observable,
      publisher: observable,
      publicationYear: observable,
      resourceType: observable,
      resourceTypeGeneral: observable,
      url: observable,
      state: observable,
      subjects: observable,
      descriptions: observable,
      alternateIdentifiers: observable,
      dates: observable,
      geoLocations: observable,
      _links: observable,
      editing: observable,
      customFieldsOnPublicPage: observable,
      linked: observable,
      requiredFields: computed,
      isValid: computed,
      recommendedFields: computed,
      anyRecommendedGiven: computed,
      requiredCompleted: computed,
      optionalCompleted: computed,
      doiTypeLabel: computed,
      setEditing: action,
      setCreatorName: action,
      setCreatorType: action,
      setTitle: action,
      setPublisher: action,
      setPublicationYear: action,
      setResourceType: action,
      setSubjects: action,
      setDescriptions: action,
      setAlternateIdentifiers: action,
      setDates: action,
      setGeoLocations: action,
      updateState: action,
      publish: action,
      retract: action,
      refresh: action,
    });

    this.parentGlobalId = parentGlobalId;
    this.id = attrs.id;
    this.rsPublicId = attrs.rsPublicId;
    this.doi = attrs.doi;
    this.doiType = attrs.doiType;
    this.creatorName = attrs.creatorName;
    this.creatorType = attrs.creatorType;
    this.creatorAffiliation = attrs.creatorAffiliation;
    this.creatorAffiliationIdentifier = attrs.creatorAffiliationIdentifier;
    this.title = attrs.title;
    this.publicUrl = attrs.publicUrl;
    this.providerUrl = attrs.providerUrl;
    this.publisher = attrs.publisher;
    // a stringified null/undefined would be truthy, so an absent year would pass the
    // required-field check and render as "undefined" in the form
    this.publicationYear =
      attrs.publicationYear === null || attrs.publicationYear === undefined ? "" : `${attrs.publicationYear}`;
    this.resourceType = attrs.resourceType;
    this.resourceTypeGeneral = attrs.resourceTypeGeneral;
    this.url = attrs.url;
    this.state = attrs.state;
    /* falling back to empty array as backend may return null */
    this.subjects = attrs.subjects ?? [];
    this.descriptions = attrs.descriptions ?? [];
    this.alternateIdentifiers = attrs.alternateIdentifiers ?? [];
    this.dates =
      attrs.dates?.map((d) => {
        return { value: new Date(d.value), type: d.type };
      }) ?? [];
    this.geoLocations = attrs.geoLocations?.map((gl) => new GeoLocationModel(gl)) ?? [];
    this._links = attrs._links;
    this.customFieldsOnPublicPage = attrs.customFieldsOnPublicPage;
    this.externalMetadataUpdate = attrs.externalMetadataUpdate ?? null;
    // server-owned; absent in an older payload means an identifier RSpace minted itself
    this.linked = attrs.linked ?? false;

    if (ApiServiceBase) {
      this.ApiServiceBase = ApiServiceBase;
    }
  }

  get requiredFields(): Array<IdentifierField> {
    return [
      /* editable value, need handler */
      {
        key: "Creator Name",
        value: this.creatorName,
        handler: (v) => this.setCreatorName(v as string),
      },
      {
        key: "Creator Type",
        value: this.creatorType,
        handler: (v) => this.setCreatorType(v as CreatorType),
        radioOptions: getCreatorTypeOptions(),
      },
      {
        key: "Name",
        value: this.title,
        handler: (v) => this.setTitle(v as string),
      },
      /*
       * Publisher and Publication Year are DataCite minting metadata that the panel does not show
       * for a B2INST identifier, nor for a linked one of any registry since RSpace cannot supply
       * them. Requiring what is never shown only produced an unactionable "details are missing"
       * warning (RSDEV-1326).
       */
      ...(this.linked || this.doiType === "PIDINST_B2INST"
        ? []
        : [
            {
              key: "Publisher",
              value: this.publisher,
              handler: (v: unknown) => this.setPublisher(v as string),
            },
            {
              key: "Publication Year",
              value: this.publicationYear,
              handler: (v: unknown) => this.setPublicationYear(v as string),
              isValid: (v: unknown) => typeof v === "string" && /^\d\d\d\d$/.test(v),
            },
          ]),
      {
        key: "Resource Type",
        value: this.resourceType,
        handler: (v) => this.setResourceType(v as string),
      },
    ];
  }

  get requiredCompleted(): boolean {
    return this.requiredFields.every((f) => Boolean(f.value));
  }

  /* some recommended fields may have required values */
  get optionalCompleted(): boolean {
    if (!this.alternateIdentifiers?.length && !this.geoLocations?.length) return true;
    return (
      (this.geoLocations && this.geoLocations.length > 0 ? this.geoLocations.every((gl) => gl.isValid) : true) &&
      (this.alternateIdentifiers && this.alternateIdentifiers.length > 0
        ? this.alternateIdentifiers.every((id) => Boolean(id.freeType))
        : true)
    );
  }

  get isValid(): boolean {
    return this.requiredCompleted && this.optionalCompleted;
  }

  get recommendedFields(): Array<IdentifierField> {
    return [
      /**
       * editable value, need handler
       * handler performs array replacement
       * duplicates in values array are not allowed
       */
      {
        key: "Subjects",
        value: this.subjects,
        handler: (v) => this.setSubjects(v as Array<IdentifierSubject>),
      },
      {
        key: "Descriptions",
        value: this.descriptions,
        handler: (v) => this.setDescriptions(v as Array<IdentifierDescription>),
        options: getIdentifierDescriptionOptions(),
        selectLabelLabel: "Description Type",
      },
      {
        key: "Alternate Identifiers",
        value: this.alternateIdentifiers,
        handler: (v) => this.setAlternateIdentifiers(v as Array<AlternateIdentifier>),
      },
      {
        key: "Dates",
        value: this.dates,
        handler: (v) => this.setDates(v as Array<IdentifierDate>),
        options: getIdentifierDateOptions(),
        selectLabelLabel: "Event Type",
      },
      {
        key: "Geolocations",
        value: this.geoLocations,
        handler: (v) => {
          if (Array.isArray(v))
            this.setGeoLocations(
              v.filter((geoLocation): geoLocation is GeoLocationModel => geoLocation instanceof GeoLocationModel),
            );
        },
      },
    ];
  }

  get anyRecommendedGiven(): boolean {
    return [this.subjects, this.descriptions, this.alternateIdentifiers, this.dates, this.geoLocations].some(
      (recommendedGroup) => Array.isArray(recommendedGroup) && recommendedGroup.length > 0,
    );
  }

  get doiTypeLabel(): string {
    return this.doiType.split("_")[1];
  }

  setEditing(value: boolean) {
    this.editing = value;
  }

  setCreatorName(name: string) {
    this.creatorName = name;
  }

  setCreatorType(type: CreatorType) {
    this.creatorType = type;
  }

  setTitle(title: string) {
    this.title = title;
  }

  setPublisher(publisher: string) {
    this.publisher = publisher;
  }

  setPublicationYear(year: string) {
    this.publicationYear = year;
  }

  setResourceType(type: string) {
    this.resourceType = type;
  }

  updateState(value: PublishingState) {
    this.state = value;
  }

  setSubjects(subjects: Array<IdentifierSubject>) {
    this.subjects = subjects;
  }

  setDescriptions(descriptions: Array<IdentifierDescription>) {
    this.descriptions = descriptions;
  }

  setAlternateIdentifiers(alternateIdentifiers: Array<AlternateIdentifier>) {
    this.alternateIdentifiers = alternateIdentifiers;
  }

  setDates(dates: Array<IdentifierDate>) {
    this.dates = dates;
  }

  setGeoLocations(geoLocations: ReadonlyArray<GeoLocation>) {
    this.geoLocations = geoLocations;
  }

  /*
   * We pass in various functions that would normally be pulled directly from
   * the UiStore as this class is used on the public page where the global
   * stores are not available as the user is not authenticated.
   */
  async publish({
    confirm,
    addAlert,
    onPublished,
  }: {
    confirm: (title: React.ReactNode, message: React.ReactNode, yesLabel: string, noLabel: string) => Promise<boolean>;
    addAlert: (alert: Alert) => void;
    onPublished?: () => void;
  }): Promise<void> {
    if (!this.ApiServiceBase) throw new Error("This operation requires the user be authenticated");
    const ApiServiceBase = this.ApiServiceBase;
    try {
      if (
        await confirm(
          i18n.t("inventory:identifierConfirm.publish.title"),
          <TransRichText
            i18nKey={
              this.doiType === "PIDINST_B2INST"
                ? "inventory:identifierConfirm.publish.bodyPidinstB2Inst"
                : this.doiType === "PIDINST_DATACITE"
                  ? "inventory:identifierConfirm.publish.bodyPidinst"
                  : "inventory:identifierConfirm.publish.body"
            }
          />,
          i18n.t("common:actions.ok"),
          i18n.t("common:actions.cancel"),
        )
      ) {
        if (!this.id) throw new Error("DOI Id must be known.");
        const response = await ApiServiceBase.post<{
          state: PublishingState;
          url: string;
          publicUrl: string;
          creatorAffiliation: string | null;
          creatorAffiliationIdentifier: string | null;
        }>(`/identifiers/${this.id}/publish`, {});
        const { state, url, publicUrl, creatorAffiliation, creatorAffiliationIdentifier } = response.data;
        this.updateState(state);
        if (state === "findable") {
          this.creatorAffiliation = creatorAffiliation;
          this.creatorAffiliationIdentifier = creatorAffiliationIdentifier;
        }
        runInAction(() => {
          this.url = url;
          this.publicUrl = publicUrl;
        });
        addAlert(
          mkAlert({
            message: i18n.t("inventory:identifierModel.alerts.published", { doi: this.doi }),
            variant: "success",
          }),
        );
        onPublished?.();
      }
    } catch (error) {
      // in case of errors like 422 the server provides a specific response message that we want to display
      addAlert(
        mkAlert({
          title: i18n.t("inventory:identifierModel.alerts.publishFailed"),
          message: getErrorMessage(error, i18n.t("inventory:errors.unknownReason")),
          variant: "error",
        }),
      );
      throw new Error("An error occurred while publishing the identifier", {
        cause: error,
      });
    }
  }

  /*
   * We pass in various functions that would normally be pulled directly from
   * the UiStore as this class is used on the public page where the global
   * stores are not available as the user is not authenticated.
   */
  async retract({
    confirm,
    addAlert,
  }: {
    confirm: (title: React.ReactNode, message: React.ReactNode, yesLabel: string, noLabel: string) => Promise<boolean>;
    addAlert: (alert: Alert) => void;
  }): Promise<void> {
    if (!this.ApiServiceBase) throw new Error("This operation requires the user be authenticated");
    const ApiServiceBase = this.ApiServiceBase;
    try {
      if (
        await confirm(
          i18n.t("inventory:identifierConfirm.retract.title"),
          <TransRichText
            i18nKey={
              this.doiType === "PIDINST_B2INST"
                ? "inventory:identifierConfirm.retract.bodyPidinstB2Inst"
                : this.doiType === "PIDINST_DATACITE"
                  ? "inventory:identifierConfirm.retract.bodyPidinst"
                  : "inventory:identifierConfirm.retract.body"
            }
          />,
          i18n.t("common:actions.ok"),
          i18n.t("common:actions.cancel"),
        )
      ) {
        if (!this.id) throw new Error("DOI Id must be known.");
        const response = await ApiServiceBase.post<{
          state: PublishingState;
        }>(`/identifiers/${this.id}/retract`, {});
        this.updateState(response.data.state);
        addAlert(
          mkAlert({
            message: i18n.t("inventory:identifierModel.alerts.retracted", { doi: this.doi }),
            variant: "success",
          }),
        );
      }
    } catch (error) {
      addAlert(
        mkAlert({
          title: i18n.t("inventory:identifierModel.alerts.retractFailed"),
          message: getErrorMessage(error, i18n.t("inventory:errors.unknownReason")),
          variant: "error",
        }),
      );
      throw new Error("An error occurred while retracting the identifier", {
        cause: error,
      });
    }
  }

  /**
   * This method simply calls both the /retract and then the /publish endpoints,
   * so that the UI may provide a single button to trigger the two actions one
   * after the other. This is simply a usability enhancement as it may not be
   * immediately obvious to users that they must retract the IGSN to be able to
   * update the metadata. The reason that this method doesn't just call the
   * `retract` and `publish` methods above is that we don't need to display
   * alerts because there is no change to the visibility of the data and the
   * error handling is slightly more complex.
   *
   * We pass in various functions that would normally be pulled directly from
   * the UiStore as this class is used on the public page where the global
   * stores are not available as the user is not authenticated.
   */
  async republish({ addAlert }: { addAlert: (alert: Alert) => void }): Promise<void> {
    if (!this.ApiServiceBase) throw new Error("This operation requires the user be authenticated");
    const ApiServiceBase = this.ApiServiceBase;
    if (this.id === null || typeof this.id === "undefined") {
      addAlert(
        mkAlert({
          title: i18n.t("inventory:identifierModel.alerts.republishFailed"),
          message: i18n.t("inventory:identifierModel.alerts.doiRequired"),
          variant: "error",
        }),
      );
    } else {
      const id: number = this.id;

      // retract
      try {
        const response = await ApiServiceBase.post<{
          state: PublishingState;
        }>(`/identifiers/${id}/retract`, {});
        this.updateState(response.data.state);
      } catch (error) {
        addAlert(
          mkAlert({
            title: i18n.t("inventory:identifierModel.alerts.republishFailed"),
            message: getErrorMessage(error, i18n.t("inventory:errors.unknownReason")),
            variant: "error",
          }),
        );
        throw new Error("An error occurred while retracting the identifier", {
          cause: error,
        });
      }

      // publish
      try {
        const response = await ApiServiceBase.post<{
          state: PublishingState;
          url: string;
          publicUrl: string;
          creatorAffiliation: string | null;
          creatorAffiliationIdentifier: string | null;
        }>(`/identifiers/${id}/publish`, {});
        const { state, url, publicUrl, creatorAffiliation, creatorAffiliationIdentifier } = response.data;
        this.updateState(state);
        if (state === "findable") {
          this.creatorAffiliation = creatorAffiliation;
          this.creatorAffiliationIdentifier = creatorAffiliationIdentifier;
        }
        runInAction(() => {
          this.url = url;
          this.publicUrl = publicUrl;
        });
        addAlert(
          mkAlert({
            message: i18n.t("inventory:identifierModel.alerts.republished", { doi: this.doi }),
            variant: "success",
          }),
        );
      } catch (error) {
        /*
         * It is possible, although unlikely, that the retract step could succeed and then the
         * publish step could fail. Should that happen, the identifier will be left in a registered
         * state and the user will need to manually re-trigger a publish step.
         */
        addAlert(
          mkAlert({
            title: i18n.t("inventory:identifierModel.alerts.republishFailed"),
            message: i18n.t("inventory:identifierModel.alerts.republishAfterRetractFailed", {
              reason: getErrorMessage(error, i18n.t("inventory:errors.unknownReason")),
            }),
            variant: "error",
          }),
        );
        throw new Error("An error occurred while republishing the identifier", {
          cause: error,
        });
      }
    }
  }

  /**
   * Re-reads the identifier's status from the provider via the refresh endpoint and applies the
   * returned state and URLs (RSDEV-1260). Unlike publish/retract this does not rethrow: nothing
   * awaits the outcome beyond the button spinner, and the alert already reports the failure.
   */
  async refresh({ addAlert }: { addAlert: (alert: Alert) => void }): Promise<void> {
    if (!this.ApiServiceBase) throw new Error("This operation requires the user be authenticated");
    if (this.id === null || typeof this.id === "undefined") throw new Error("DOI Id must be known.");
    try {
      const response = await this.ApiServiceBase.post<{
        state: PublishingState;
        url: string | null;
        publicUrl: string | null;
        providerUrl: string | null;
      }>(`/identifiers/${this.id}/refresh`, {});
      const { state, url, publicUrl, providerUrl } = response.data;
      runInAction(() => {
        this.state = state;
        this.url = url;
        this.publicUrl = publicUrl;
        this.providerUrl = providerUrl;
      });
      addAlert(
        mkAlert({
          message: i18n.t("inventory:identifierModel.alerts.refreshed", {
            state: stateLabelOf(state),
          }),
          variant: "success",
        }),
      );
    } catch (error) {
      addAlert(
        mkAlert({
          title: i18n.t("inventory:identifierModel.alerts.refreshFailed"),
          message: getErrorMessage(error, i18n.t("inventory:errors.unknownReason")),
          variant: "error",
        }),
      );
    }
  }

  toJson(): object {
    return {
      parentGlobalId: this.parentGlobalId,
      id: this.id,
      rsPublicId: this.rsPublicId,
      doi: this.doi,
      doiType: this.doiType,
      creatorName: this.creatorName,
      creatorType: this.creatorType,
      creatorAffiliation: this.creatorAffiliation,
      creatorAffiliationIdentifier: this.creatorAffiliationIdentifier,
      title: this.title,
      publicUrl: this.publicUrl,
      providerUrl: this.providerUrl,
      publisher: this.publisher,
      publicationYear: this.publicationYear,
      resourceType: this.resourceType,
      resourceTypeGeneral: this.resourceTypeGeneral,
      url: this.url,
      state: this.state,
      subjects: this.subjects,
      descriptions: this.descriptions,
      alternateIdentifiers: this.alternateIdentifiers,
      dates: this.dates?.map((d) => ({
        ...d,
        value: d.value instanceof Date && isValid(d.value) ? format(d.value, "yyyy-MM-dd") : d.value,
      })),
      geoLocations: this.geoLocations?.map((g) => g.toJson()),
      editing: this.editing,
      customFieldsOnPublicPage: this.customFieldsOnPublicPage,
    };
  }
}

/**
 * One external PIDINST metadata update worth telling the user about, classified but not yet
 * rendered (RSDEV-1356). `UPDATED` and an absent update produce no report at all, because success
 * is deliberately silent and absence means nothing was attempted.
 *
 * Kept separate from the toasts so the single-record and bulk callers can present the same
 * classification differently without either one restating the outcome-to-variant mapping.
 */
export type ExternalMetadataUpdateReport = {
  doi: string;
  variant: "error" | "notice";
  /** A localized sentence from the server, ready to show verbatim. */
  reason: string;
};

/**
 * Classifies the external PIDINST metadata updates attempted by the save or transfer that returned
 * these identifiers. The variant is decided by `outcome`, never by the wording of `reason`.
 *
 * A record frozen by its own state is a `notice`: normal and expected. Every other outcome,
 * including one added to the server's enum after this build shipped, is an `error`. That
 * fall-through is deliberate: staying quiet about an outcome the UI does not recognise would
 * reinstate the silent drift this feature exists to close (ADR 0008).
 *
 * A pure function rather than a method, so this module stays free of the global stores (see the
 * note at the top of the file) and each caller raises its own alerts after its own success toast.
 */
export const externalMetadataUpdateReports = (
  identifiers: ReadonlyArray<Identifier>,
): Array<ExternalMetadataUpdateReport> =>
  identifiers.flatMap((id) => {
    const update = id.externalMetadataUpdate;
    if (!update || update.outcome === "UPDATED") return [];
    return [
      {
        doi: id.doi,
        variant: update.outcome === "NOT_UPDATABLE" ? ("notice" as const) : ("error" as const),
        reason: update.reason,
      },
    ];
  });

/**
 * The title for one report, naming the identifier it belongs to. Shared by the single toast and by
 * a grouped alert's detail rows, where two identifiers on one record are otherwise
 * indistinguishable.
 */
export const externalMetadataUpdateTitle = ({ doi, variant }: ExternalMetadataUpdateReport): string =>
  variant === "error"
    ? i18n.t("inventory:identifierModel.alerts.externalUpdateFailed", { doi })
    : i18n.t("inventory:identifierModel.alerts.externalUpdateNotPossible", { doi });

/**
 * The toast for one report. Neither variant expires on a timer: both carry a full sentence to read
 * and no reading speed can be assumed for it (WCAG 2.2.1).
 */
export const externalMetadataUpdateAlert = (report: ExternalMetadataUpdateReport): Alert =>
  mkAlert({
    title: externalMetadataUpdateTitle(report),
    message: report.reason,
    variant: report.variant,
    isInfinite: true,
  });

/** One toast per identifier. A bulk caller should group the reports instead. */
export const externalMetadataUpdateAlerts = (identifiers: ReadonlyArray<Identifier>): Array<Alert> =>
  externalMetadataUpdateReports(identifiers).map(externalMetadataUpdateAlert);
